import FileUpload from 'components/fileUpload/FileUpload';
import { FileMetaData, FileType } from 'components/fileUpload/strategies/strategies';
import Hint from 'components/Hint';
import { ErrorNotifierSlim } from 'components/notifiers/ErrorNotifierSlim';
import useModifyRequest from 'hooks/useModifyRequest';
import useUploadRequest from 'hooks/useUploadRequest';
import { logError } from 'lib/debug-helpers';
import React from 'react';
import styled from 'styled-components';
import { sizes } from 'theme';
import strings from '../../../strings/strings.json';
import { FlexColMd, FlexEnd } from 'components/Elements';
import useNeveForm from 'components/forms/NeveForm';
import { CreateCourseInput, Form } from 'pages/trainer/components/CreateCourseForm';
import TextField from 'components/forms/TextField';
import { createErrorMessage, fieldErrorCodes, requiredFieldErrorMessage } from 'shared/error-messages';
import { capitalsRegex } from 'components/accessibilityGuide/utils';
import AccessibilityNotifier from 'components/notifiers/AccessibilityNotifier';
import Button from 'components/buttons/Button';

type CreateCourseUrl = 'courses/create-with-scorm' | 'courses/create-with-powerpoint';

type CreateCourseWithFileFormProps = {
    createCourseUrl: CreateCourseUrl;
    closeModal: (courseId: string) => void;
};

type CreateCourseWithFileRequest = {
    courseTitle: string;
    fileName: string;
    s3Key: string;
};

export default function CreateCourseWithFileForm({
    createCourseUrl,
    closeModal,
}: CreateCourseWithFileFormProps): JSX.Element {
    let hint: string;
    let fileType: FileType;

    switch (createCourseUrl) {
        case 'courses/create-with-scorm':
            hint = strings.createCourseModal.createCourseForm.hint.SCORM;
            fileType = FileType.SCORM;
            break;
        case 'courses/create-with-powerpoint':
            hint = strings.createCourseModal.createCourseForm.hint.PowerPoint;
            fileType = FileType.POWERPOINT;
            break;
    }

    const { upload, uploading } = useUploadRequest();
    const { modifyData: createCourseWithFile, loading: creating } = useModifyRequest<
        CreateCourseWithFileRequest,
        string
    >(createCourseUrl, 'POST');

    const [tempFileFromLocal, setTempFileFromLocal] = React.useState<File | undefined>();
    const [fileValid, setFileValid] = React.useState<boolean>(false);
    const [fileMetaDataValid, setFileMetaDataValid] = React.useState<boolean>(false);
    const [fileMetaData, setFileMetaData] = React.useState<FileMetaData | undefined>(undefined);
    const [error, setError] = React.useState<string | null>(null);

    const {
        register,
        watch,
        handleSubmit,
        formState: { errors },
    } = useNeveForm<CreateCourseInput>();

    React.useEffect(() => {
        if (tempFileFromLocal) {
            setError('');
        }
    }, [tempFileFromLocal]);

    async function onSubmit(data: CreateCourseInput) {
        setError(null);
        if (!tempFileFromLocal) {
            setError(strings.fileUploadPage.noFileText);
            return;
        }
        if (!fileValid) return;
        if (!fileMetaDataValid) return;

        const uploadResponse = await upload(tempFileFromLocal);

        if (uploadResponse.errors?.length) {
            logError(uploadResponse.errors);
            setError(strings.createCourseModal.createCourseForm.error);
            return;
        }
        if (!uploadResponse.value) {
            setError(strings.createCourseModal.createCourseForm.error);
            return;
        }

        const { errors, value } = await createCourseWithFile({
            courseTitle: data.courseTitle,
            fileName: uploadResponse.value.filename,
            s3Key: uploadResponse.value.s3Key,
        });
        if (errors) {
            logError(errors);
            setError(strings.createCourseModal.createCourseForm.error);
            return;
        }

        closeModal(value!);
    }

    const courseTitle = watch('courseTitle');
    const courseTitleMaxLength = 120;

    return (
        <FlexColMd>
            <Form onSubmit={handleSubmit(onSubmit)}>
                <TextField
                    maxLength={courseTitleMaxLength}
                    fieldName="courseTitle"
                    labelText={strings.createCoursePage.courseNameInputLabel}
                    inputAria={strings.createCoursePage.courseNameInputLabelAria}
                    errorMessage={errors.courseTitle?.message}
                    required
                    {...register('courseTitle', {
                        required: {
                            value: true,
                            message: requiredFieldErrorMessage(strings.createCoursePage.courseNameInputLabel),
                        },
                        maxLength: {
                            value: courseTitleMaxLength,
                            message: createErrorMessage(
                                strings.createCoursePage.courseNameInputLabel,
                                fieldErrorCodes.maxLength,
                            ),
                        },
                    })}
                />
                {capitalsRegex.test(courseTitle) && <AccessibilityNotifier accessibilityGuideKey={'textAllCaps'} />}
                <Hint hint={hint} />
                <FileUpload
                    localFile={tempFileFromLocal}
                    setLocalFile={(newFile: File | undefined) => {
                        setTempFileFromLocal(newFile);
                    }}
                    fileMetaData={fileMetaData}
                    setFileMetaData={(newFileMetaData: FileMetaData | undefined) => {
                        setFileMetaData(newFileMetaData);
                    }}
                    fileValid={fileValid}
                    setFileValid={(fileValid: boolean) => {
                        setFileValid(fileValid);
                    }}
                    fileMetaDataValid={fileMetaDataValid}
                    setFileMetaDataValid={(fileMetaDataValid: boolean) => {
                        setFileMetaDataValid(fileMetaDataValid);
                    }}
                    chosenFileType={fileType}
                />
                <ButtonRow>
                    {error && <ErrorNotifierSlim description={error} />}
                    <Button
                        title={strings.createCourseModal.createButton.title}
                        aria={strings.createCourseModal.createButton.aria}
                        submitting={uploading || creating}
                        type="submit"
                    />
                </ButtonRow>
            </Form>
        </FlexColMd>
    );
}

const ButtonRow = styled(FlexEnd)`
    gap: ${sizes.spacingMd};
`;
